import { Button } from "@svea/design-system-components-react";
import { P, Stack, SvgIcon, Text, useText } from "@sveabank/cc-common";
import { usePageContext } from "../../../hooks/context";
import { useUtils } from "../../../hooks/useUtils";
import type { OnboardingPackageId } from "../../../types/package";
import { useChoosePackage } from "../hooks";

type Props = {
	packageId: OnboardingPackageId;
	price: string;
	isRejected?: boolean;
	isPromoted?: boolean;
};

const PackageBlockFooter = ({ packageId, price, isRejected = false, isPromoted }: Props) => {
	const [t, texts] = useText();
	const {
		useRemoveAndSelectPackageMutation,
		useSelectPackageMutation,
		selectedPackageId,
		applicationId,
	} = usePageContext(useChoosePackage);

	const [selectPackageMutation, { isLoading: selectPackageIsLoading }] = useSelectPackageMutation();
	const [removeAndSelectPackage, { isLoading: selectRemoveIsLoading }] =
		useRemoveAndSelectPackageMutation();

	const { isMutationLoading } = useUtils();

	const isSelectingPackage = selectPackageIsLoading || selectRemoveIsLoading;

	const selectPackage = async (id: string) => {
		if (selectedPackageId) {
			await removeAndSelectPackage({
				applicationId,
				packageId: id,
			}).unwrap();
		} else {
			await selectPackageMutation({
				applicationId,
				packageId: id,
			}).unwrap();
		}
	};

	const isSelected = selectedPackageId === packageId;
	const text = texts.choosePackage[packageId];
	const title = t(text.title);

	return (
		<>
			<div
				className={`tablet:text-center relative flex grow-0 flex-col items-start justify-between gap-6 border-solid text-left ${
					isPromoted ? "tablet:items-start" : "tablet:items-center"
				}`}
			>
				<Text className="text-text-headline text-2xl" bold>
					{price}
				</Text>

				{!isRejected && (
					<>
						<div className="tablet:w-auto flex min-h-[45px] w-full">
							{!isSelected ? (
								<Button
									width="full"
									data-testid={`selectPackage-${packageId}`}
									aria-label={
										isSelected || selectPackageIsLoading
											? t(texts.common.chosen)
											: t(texts.choosePackage.selectLabel, title)
									}
									variant={isSelected || selectPackageIsLoading ? "secondary" : "primary"}
									onPress={() => !isMutationLoading && selectPackage(packageId)}
									isDisabled={isSelected}
									isPending={isSelectingPackage}
								>
									<Text bold className="text-text-contrast">
										{t(texts.common.choose, title)}
									</Text>
								</Button>
							) : (
								<Stack direction="row" gap={2} align="center">
									<SvgIcon height={24} imageSrc="CHECK" className="text-foreground-success" />
									<P>{t(texts.choosePackage.packageSelected, title)}</P>
								</Stack>
							)}
						</div>

						<Stack gap={1} align="start">
							{packageId !== "FORETAGARNA-CARDONLY" && (
								<P className="text-text-secondary text-xs">
									{t(texts.choosePackage.packageBlock.terms)}
								</P>
							)}

							{packageId === "E-HANDEL-BAS" && (
								<P className="text-text-secondary text-xs">
									{t(texts.choosePackage.packageBlock.eHandelterms)}
								</P>
							)}
						</Stack>
					</>
				)}
			</div>
		</>
	);
};

export default PackageBlockFooter;
